export const businessEconomicsCourse = {
  id: "business-economics-finance-2024",
  title: "Business Economics for Finance: Complete Professional Course",
  shortDescription: "Master economic principles, market analysis, and business cycle forecasting essential for financial decision-making and investment analysis.",
  description: "Comprehensive course covering microeconomic and macroeconomic principles with specific applications to financial markets, corporate finance, and investment analysis.",
  category: "Finance",
  industryCategory: "Financial Services",
  difficulty: "intermediate",
  duration: 16,
  estimatedWeeks: 8,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from economic analysts and financial economists",
  thumbnailUrl: "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Apply microeconomic principles to business and investment decisions",
    "Analyze macroeconomic indicators and their impact on financial markets",
    "Understand business cycles and their implications for asset allocation",
    "Evaluate market efficiency and competitive dynamics",
    "Assess monetary and fiscal policy effects on financial markets",
    "Apply economic forecasting techniques to investment analysis",
    "Understand international trade and exchange rate economics",
    "Analyze industry economics and competitive positioning"
  ],

  prerequisites: [
    "Basic Mathematics",
    "Statistics Fundamentals",
    "Financial Markets Knowledge"
  ],

  skills: [
    "Economic Analysis",
    "Market Forecasting",
    "Business Cycle Analysis",
    "Policy Impact Assessment",
    "Industry Analysis",
    "Competitive Strategy",
    "Financial Modeling",
    "Risk Assessment"
  ],

  tags: [
    "Economics",
    "Business Analysis",
    "Market Economics",
    "Financial Economics",
    "Policy Analysis",
    "Industry Analysis",
    "Forecasting",
    "Business Strategy"
  ],

  marketData: {
    averageSalary: 105000,
    salaryRange: {
      min: 70000,
      max: 180000
    },
    jobDemand: 4.0,
    marketTrend: "stable",
    careerPaths: [
      "Economic Analyst",
      "Market Research Analyst",
      "Policy Analyst",
      "Business Strategist",
      "Investment Analyst",
      "Corporate Development"
    ],
    relatedSkills: [
      "Data Analysis",
      "Excel/Python",
      "Econometric Modeling",
      "Research Methods",
      "Statistical Software",
      "Financial Analysis"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "microeconomic-foundations",
      title: "Microeconomic Foundations",
      description: "Supply and demand, market structures, and firm behavior analysis",
      order: 1,
      estimatedHours: 4.0,
      content: [
        {
          id: "supply-demand-basics",
          title: "Supply and Demand Analysis",
          description: "Understanding market equilibrium and price determination",
          type: "video",
          duration: 30
        },
        {
          id: "market-structures",
          title: "Market Structures and Competition",
          description: "Analyzing different competitive environments and their implications",
          type: "text",
          duration: 45,
          textContent: `# Market Structures and Competition

## Perfect Competition

### Characteristics
**Perfect Competition Assumptions:**
- Large number of buyers and sellers
- Homogeneous products (perfect substitutes)
- Perfect information available to all participants
- Free entry and exit from the market
- Price-taking behavior (no individual market power)

**Market Outcomes:**
- **Price = Marginal Cost**: Allocative efficiency
- **Economic Profit = Zero**: Long-run equilibrium
- **Consumer Surplus Maximized**: Welfare optimal outcome
- **Perfect Resource Allocation**: No deadweight losses

### Real-World Applications
**Agricultural Markets:**
- Commodity exchanges (wheat, corn, soybeans)
- Standardized products and grades
- Many producers and buyers
- Price transparency through exchanges

**Financial Markets:**
- Foreign exchange markets
- Government bond markets
- Large-cap equity markets
- High liquidity and information efficiency

## Monopolistic Competition

### Market Characteristics
**Key Features:**
- Many firms competing
- Differentiated products (brands, quality, features)
- Some pricing power due to differentiation
- Relatively easy market entry and exit
- Monopolistic competition in short run, competitive in long run

**Pricing and Output Decisions:**
- **Price > Marginal Cost**: Some market power
- **Economic Profit = Zero**: Long-run equilibrium with entry
- **Product Differentiation**: Creating customer loyalty
- **Marketing and Branding**: Investment in differentiation

### Examples and Analysis
**Retail Industry:**
- Restaurants and food services
- Clothing and apparel
- Personal care products
- Local service businesses

**Financial Services:**
- Bank branches and consumer banking
- Insurance products and services
- Investment advisory services
- Fintech applications and platforms

## Oligopoly Markets

### Oligopoly Characteristics
**Market Structure:**
- Few dominant firms (typically 3-8 major players)
- Significant barriers to entry
- Interdependent decision making
- Products may be differentiated or standardized
- Potential for collusive behavior

**Strategic Interactions:**
- **Game Theory Applications**: Nash equilibrium, prisoner's dilemma
- **Price Leadership**: Dominant firm sets price, others follow
- **Kinked Demand Curve**: Price stickiness in oligopoly
- **Collusion vs. Competition**: Cartel stability issues

### Industry Examples
**Technology Sector:**
- Smartphone manufacturers (Apple, Samsung, Google)
- Cloud computing (Amazon, Microsoft, Google)
- Social media platforms (Meta, Twitter, TikTok)
- Operating systems (Microsoft, Apple, Google)

**Financial Services:**
- Credit card networks (Visa, Mastercard, AmEx)
- Credit rating agencies (S&P, Moody's, Fitch)
- Investment banks (Goldman Sachs, JPMorgan, Morgan Stanley)
- Insurance companies (regional/national markets)

**Traditional Industries:**
- Airlines (route-specific oligopolies)
- Telecommunications (wireless carriers)
- Automotive (global and regional markets)
- Energy/Oil (integrated oil companies)

## Monopoly Markets

### Monopoly Characteristics
**Pure Monopoly Features:**
- Single seller in the market
- No close substitutes available
- High barriers to entry
- Price-making ability (not price-taking)
- Potential for economic profits

**Sources of Monopoly Power:**
- **Natural Monopoly**: Economies of scale make single firm efficient
- **Legal Monopoly**: Patents, copyrights, government franchises
- **Resource Control**: Exclusive access to key inputs
- **Network Effects**: Value increases with user adoption

### Monopoly Pricing and Welfare
**Pricing Strategy:**
- **Profit Maximization**: MR = MC (not P = MC)
- **Price > Marginal Cost**: Deadweight welfare loss
- **Consumer Surplus Reduced**: Transfer to monopoly profits
- **Price Discrimination**: Extracting consumer surplus

**Regulation and Antitrust:**
- **Natural Monopoly Regulation**: Utility price controls
- **Antitrust Law**: Preventing monopolization
- **Breaking Up Monopolies**: Historical examples (AT&T, Standard Oil)
- **Modern Tech Monopoly Debates**: Platform dominance issues

### Examples in Finance and Business
**Natural Monopolies:**
- Public utilities (electricity, water, gas)
- Transportation infrastructure (airports, ports)
- Payment systems and clearing networks
- Central banks and monetary policy

**Intellectual Property Monopolies:**
- Pharmaceutical patents
- Software and technology patents  
- Financial innovation and process patents
- Branded financial products

## Market Power and Competitive Dynamics

### Measuring Market Power
**Concentration Ratios:**
- **Four-Firm Concentration Ratio**: Market share of top 4 firms
- **Herfindahl-Hirschman Index (HHI)**: Sum of squared market shares
- **Lerner Index**: (P - MC) / P measures markup over marginal cost

**HHI Interpretation:**
- HHI < 1,500: Unconcentrated (competitive)
- 1,500 ≤ HHI < 2,500: Moderately concentrated
- HHI ≥ 2,500: Highly concentrated (potential antitrust concern)

### Competitive Strategy Applications
**Porter's Five Forces Framework:**
1. **Threat of New Entrants**: Barriers to entry analysis
2. **Bargaining Power of Suppliers**: Input cost and availability
3. **Bargaining Power of Buyers**: Customer concentration and switching costs
4. **Threat of Substitutes**: Alternative products and technologies
5. **Industry Rivalry**: Competitive intensity and profitability

**Strategic Implications:**
- **Differentiation Strategy**: Creating unique value propositions
- **Cost Leadership**: Achieving lowest cost position
- **Focus Strategy**: Serving specific market segments
- **Blue Ocean Strategy**: Creating uncontested market space

## Financial Market Applications

### Banking Industry Structure
**Traditional Banking:**
- Local markets often oligopolistic
- Regional and national competition
- Regulatory barriers to entry
- Economies of scale in operations

**Investment Banking:**
- Global oligopoly structure
- High barriers to entry (capital, relationships)
- Product differentiation through expertise
- Network effects and reputation

### Insurance Markets
**Market Structure Variation:**
- **Auto Insurance**: Regulated oligopolies by state
- **Life Insurance**: National competition with differentiation
- **Reinsurance**: Global oligopoly with few major players
- **Specialty Insurance**: Niche monopolistic competition

### Asset Management
**Mutual Fund Industry:**
- Monopolistic competition with brand differentiation
- Economies of scale favor larger firms
- Product proliferation and specialization
- Distribution channel importance

**Index Fund Market:**
- Oligopoly dominated by Vanguard, BlackRock, State Street
- Economies of scale crucial for low-cost provision
- Network effects through ETF ecosystems
- Regulatory considerations for market concentration

Understanding market structures is essential for analyzing competitive dynamics, pricing strategies, and long-term industry profitability in financial and business decision-making.`
        },
        {
          id: "elasticity-calculator",
          title: "Price Elasticity Analysis Tool",
          description: "Calculate price elasticity of demand and analyze market responsiveness",
          type: "finance-interactive",
          duration: 35,
          interactiveData: {
            exerciseType: "elasticity-analysis",
            instructions: "Calculate price elasticity of demand for different products and analyze the implications for pricing strategy and revenue optimization.",
            scenario: {
              products: [
                { name: "Luxury Cars", initialPrice: 50000, newPrice: 55000, initialQuantity: 1000, newQuantity: 850 },
                { name: "Gasoline", initialPrice: 3.00, newPrice: 3.50, initialQuantity: 10000, newQuantity: 9200 },
                { name: "Basic Food", initialPrice: 5.00, newPrice: 6.00, initialQuantity: 2000, newQuantity: 1900 },
                { name: "Netflix Subscription", initialPrice: 15, newPrice: 18, initialQuantity: 5000, newQuantity: 4200 }
              ]
            },
            hints: [
              "Elasticity = (% Change in Quantity) / (% Change in Price)",
              "Elastic demand: |E| > 1, Inelastic: |E| < 1",
              "Elastic goods: price increases reduce total revenue",
              "Inelastic goods: price increases raise total revenue"
            ]
          }
        }
      ],
      quiz: {
        id: "microeconomic-quiz",
        title: "Microeconomic Foundations Quiz",
        description: "Test your understanding of market structures and competition",
        timeLimit: 15,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "In perfect competition, firms are price takers because:",
            options: [
              "They have significant market power",
              "Products are differentiated",
              "There are many firms selling identical products",
              "Barriers to entry are high"
            ],
            correctAnswer: 2,
            explanation: "In perfect competition, the large number of firms selling identical products means no single firm can influence market price - they must accept the market price."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Which market structure typically results in the highest prices for consumers?",
            options: [
              "Perfect competition",
              "Monopolistic competition",
              "Oligopoly",
              "Monopoly"
            ],
            correctAnswer: 3,
            explanation: "Monopoly typically results in the highest prices because the monopolist can restrict output to maximize profits, with no competitive pressure to lower prices."
          }
        ]
      }
    },

    {
      id: "macroeconomic-analysis",
      title: "Macroeconomic Analysis",
      description: "GDP, inflation, unemployment, and macroeconomic policy impacts",
      order: 2,
      estimatedHours: 4.5,
      content: [
        {
          id: "gdp-economic-indicators",
          title: "GDP and Key Economic Indicators",
          description: "Understanding national income accounting and economic measurement",
          type: "video",
          duration: 40
        },
        {
          id: "monetary-fiscal-policy",
          title: "Monetary and Fiscal Policy Analysis",
          description: "Understanding policy tools and their economic impacts",
          type: "text",
          duration: 50,
          textContent: `# Monetary and Fiscal Policy Analysis

## Monetary Policy Framework

### Central Bank Objectives
**Primary Mandates:**
- **Price Stability**: Maintaining low and stable inflation (typically 2% target)
- **Full Employment**: Supporting maximum sustainable employment
- **Financial Stability**: Ensuring stable financial system functioning
- **Exchange Rate Stability**: Maintaining competitive currency values (some central banks)

**Federal Reserve Dual Mandate:**
- Maximum employment and stable prices
- Long-term interest rate stability (implicit third mandate)
- Financial stability considerations (post-2008 addition)

### Monetary Policy Tools

**Conventional Tools:**

**Federal Funds Rate (Policy Rate)**
- **Mechanism**: Central bank sets overnight interbank lending rate
- **Transmission**: Affects other interest rates throughout economy
- **Impact Channels**: 
  - Investment decisions (cost of capital)
  - Consumption decisions (savings vs. spending)
  - Exchange rate effects (international capital flows)
  - Asset prices (discount rate for valuations)

**Reserve Requirements**
- **Definition**: Minimum reserves banks must hold against deposits
- **Mechanism**: Higher requirements reduce money supply, lower requirements increase it
- **Usage**: Rarely changed in developed economies (structural tool)
- **Impact**: Affects bank lending capacity and money multiplier

**Discount Window Operations**
- **Purpose**: Emergency lending to banks at discount rate
- **Stigma Effect**: Banks reluctant to use due to market perceptions
- **Collateral Requirements**: Secured lending with eligible assets
- **Crisis Tool**: Expanded during financial emergencies

**Open Market Operations**
- **Primary Tool**: Buying/selling government securities
- **Daily Operations**: Fine-tuning money supply and interest rates
- **Permanent vs. Temporary**: Outright purchases vs. repurchase agreements
- **Market Impact**: Direct influence on bond yields and liquidity

**Unconventional Tools (Post-2008):**

**Quantitative Easing (QE)**
- **Large-Scale Asset Purchases**: Government bonds, corporate bonds, mortgage securities
- **Portfolio Balance Effect**: Lower long-term interest rates
- **Wealth Effect**: Higher asset prices boost consumer spending
- **Credit Easing**: Support specific market segments

**Forward Guidance**
- **Communication Strategy**: Guiding expectations about future policy
- **Calendar-Based**: Promises about timing of rate changes
- **State-Contingent**: Conditional on economic indicators
- **Market Impact**: Influence long-term interest rates through expectations

**Negative Interest Rates**
- **European Central Bank**: -0.5% deposit facility rate
- **Bank of Japan**: -0.1% policy rate
- **Transmission Issues**: Banking sector profitability concerns
- **Cash Conversion**: Physical currency storage costs

## Monetary Policy Transmission Mechanisms

### Interest Rate Channel
**Traditional Transmission:**
1. Central bank changes policy rate
2. Short-term market rates adjust immediately
3. Long-term rates adjust based on expectations
4. Investment and consumption respond to rate changes
5. Aggregate demand and economic activity change

**Effectiveness Factors:**
- Financial market development and integration
- Banking sector health and lending capacity
- Corporate and household debt levels
- Interest rate sensitivity of investment and consumption

### Credit Channel
**Bank Lending Channel:**
- Monetary policy affects bank reserves and lending capacity
- Credit-constrained borrowers disproportionately affected
- Small business and consumer lending most sensitive
- Bank capital and regulatory constraints matter

**Balance Sheet Channel:**
- Interest rate changes affect borrower net worth
- Higher rates reduce collateral values and creditworthiness
- External finance premium increases
- Investment and spending decline

### Asset Price Channel
**Equity Markets:**
- Lower rates reduce discount rates for equity valuation
- Portfolio rebalancing from bonds to stocks
- Wealth effects on consumption
- Cost of capital effects on investment

**Real Estate Markets:**
- Mortgage rate sensitivity to policy rates
- Housing wealth effects on consumption
- Construction and real estate investment impacts
- Regional variations in transmission

### Exchange Rate Channel
**International Capital Flows:**
- Interest rate differentials affect currency values
- Lower domestic rates lead to currency depreciation
- Import prices and inflation affected
- Export competitiveness improved

**Emerging Market Spillovers:**
- Capital flow volatility from developed country policy
- Currency crises from sudden stops
- Policy coordination challenges
- Reserve accumulation strategies

## Fiscal Policy Framework

### Fiscal Policy Tools

**Government Spending:**
- **Infrastructure Investment**: Roads, bridges, broadband, research
- **Transfer Payments**: Unemployment insurance, social security, welfare
- **Government Consumption**: Salaries, defense spending, operations
- **Multiplier Effects**: Direct spending plus indirect economic impact

**Taxation Policy:**
- **Income Taxes**: Progressive, proportional, or regressive structures
- **Corporate Taxes**: Business investment and location incentives
- **Consumption Taxes**: Sales taxes, VAT, excise taxes
- **Capital Taxes**: Capital gains, wealth taxes, property taxes

**Automatic Stabilizers:**
- **Progressive Income Tax**: Higher earners pay higher rates during booms
- **Unemployment Insurance**: Payments increase during recessions
- **Transfer Programs**: Welfare spending rises during economic downturns
- **Cyclical Budget Balance**: Deficits during recessions, surpluses during booms

### Fiscal Multipliers

**Government Spending Multipliers:**
- **Infrastructure**: High multipliers (1.4-2.2) due to employment effects
- **Transfer Payments**: Moderate multipliers (0.8-1.2) based on propensity to consume
- **Tax Cuts**: Lower multipliers (0.3-0.8) due to saving and import leakage

**Factors Affecting Multiplier Size:**
- **Economic Slack**: Higher multipliers during recessions
- **Monetary Policy Accommodation**: Lower interest rates amplify fiscal effects
- **Trade Openness**: Import leakage reduces domestic multipliers
- **Debt Sustainability**: High debt levels may reduce multiplier effectiveness

## Policy Coordination and Conflicts

### Monetary-Fiscal Coordination

**Complementary Policies:**
- **Economic Expansion**: Loose monetary + loose fiscal policy
- **Economic Contraction**: Tight monetary + tight fiscal policy
- **Crisis Response**: Coordinated stimulus (2008, 2020 examples)

**Policy Conflicts:**
- **Fiscal Expansion + Monetary Tightening**: Higher interest rates offset spending
- **Fiscal Austerity + Monetary Easing**: Mixed signals and reduced effectiveness
- **Political Business Cycles**: Electoral considerations affecting policy timing

### International Policy Spillovers

**Developed Economy Policies:**
- **Fed Policy Impact**: Global dollar funding and emerging market flows
- **ECB Policies**: Eurozone integration and peripheral country effects
- **Policy Divergence**: Different timing of rate changes creates volatility

**Currency Wars Concerns:**
- **Competitive Devaluation**: Beggar-thy-neighbor policies
- **Trade Imbalances**: Savings-investment gaps and current accounts
- **Reserve Currency Privilege**: Dollar dominance in international transactions

## Financial Market Implications

### Bond Markets
**Yield Curve Effects:**
- **Monetary Policy**: Short-end control through policy rates
- **Fiscal Policy**: Long-end impact through supply and growth expectations
- **Curve Steepening/Flattening**: Relative policy impacts
- **Credit Spreads**: Risk appetite and economic outlook effects

### Equity Markets
**Sector Rotation:**
- **Interest Rate Sensitivity**: Utilities, REITs, growth stocks
- **Fiscal Policy Beneficiaries**: Infrastructure, defense, healthcare
- **Currency Effects**: Export vs. import-dependent sectors
- **Earnings Impact**: Profit margin and revenue growth effects

### Currency Markets
**Policy Divergence Trading:**
- **Interest Rate Differentials**: Carry trade opportunities
- **Economic Outlook**: Growth and inflation expectations
- **Central Bank Communication**: Forward guidance and intervention threats
- **Safe Haven Flows**: Crisis periods and risk-off sentiment

Understanding monetary and fiscal policy frameworks is essential for analyzing economic cycles, forecasting market movements, and making informed investment and business decisions.`
        },
        {
          id: "policy-impact-simulator",
          title: "Policy Impact Analysis Simulator",
          description: "Analyze the effects of monetary and fiscal policy changes on the economy",
          type: "finance-interactive",
          duration: 45,
          interactiveData: {
            exerciseType: "policy-simulation",
            instructions: "Simulate the economic impact of different monetary and fiscal policy scenarios. Analyze the effects on GDP, inflation, employment, and financial markets.",
            scenario: {
              economicConditions: {
                gdpGrowth: 0.02,
                inflationRate: 0.08,
                unemploymentRate: 0.06,
                interestRate: 0.01
              },
              policyOptions: {
                monetary: ["Raise rates 0.5%", "Lower rates 0.5%", "Quantitative easing", "No change"],
                fiscal: ["Increase spending 5%", "Cut spending 5%", "Cut taxes 10%", "Raise taxes 10%", "No change"]
              },
              timeHorizon: 8
            },
            hints: [
              "Consider the current economic conditions when selecting policies",
              "Monetary policy affects interest rates and credit conditions",
              "Fiscal policy has direct effects on aggregate demand",
              "Policy effectiveness depends on economic slack and conditions"
            ]
          }
        }
      ]
    },

    {
      id: "business-cycles",
      title: "Business Cycles and Forecasting",
      description: "Economic cycles, leading indicators, and forecasting techniques",
      order: 3,
      estimatedHours: 4.0,
      content: [
        {
          id: "business-cycle-analysis",
          title: "Business Cycle Phases and Indicators",
          description: "Understanding expansion, peak, recession, and recovery phases",
          type: "video",
          duration: 35
        },
        {
          id: "economic-forecasting",
          title: "Economic Forecasting Techniques",
          description: "Methods for predicting economic trends and turning points",
          type: "text",
          duration: 40,
          textContent: `# Economic Forecasting Techniques

## Leading Economic Indicators

### Conference Board Leading Economic Index (LEI)

**Components of LEI (United States):**
1. **Average Weekly Hours (Manufacturing)**: Labor market efficiency indicator
2. **Initial Claims for Unemployment Insurance**: Labor market stress measure
3. **Manufacturers' New Orders (Consumer Goods/Materials)**: Demand strength
4. **ISM New Orders Index**: Business sentiment and future production
5. **Manufacturers' New Orders (Nondefense Capital Goods)**: Business investment
6. **Building Permits**: Housing market strength
7. **Stock Prices (S&P 500)**: Market confidence and wealth effects
8. **Leading Credit Index**: Credit conditions and financial stress
9. **Interest Rate Spread (10-year Treasury minus Federal Funds)**: Yield curve slope
10. **Average Consumer Expectations**: Consumer confidence

**LEI Interpretation:**
- **Rising LEI**: Economic expansion likely in 6-9 months
- **Falling LEI**: Economic slowdown or recession risk
- **Three Consecutive Declines**: Traditional recession warning signal
- **Magnitude Matters**: Larger changes indicate stronger signals

### Yield Curve as Leading Indicator

**Yield Curve Inversion:**
- **Definition**: Short-term rates > Long-term rates
- **Measurement**: 2-year vs. 10-year Treasury spread
- **Historical Record**: Inverted curve preceded last 9 recessions
- **Lead Time**: Typically 12-24 months before recession onset

**Economic Logic:**
- **Monetary Policy Tightening**: Fed raising rates to combat inflation
- **Recession Expectations**: Bond investors expect future rate cuts
- **Banking Sector Impact**: Compressed net interest margins
- **Credit Conditions**: Tightening lending standards

### Other Leading Indicators

**Financial Market Indicators:**
- **Credit Spreads**: Corporate bond spreads over Treasuries
- **Equity Market Performance**: Wealth effects and business confidence
- **Dollar Strength**: Impact on trade and competitiveness
- **Commodity Prices**: Inflation expectations and global demand

**Real Economy Indicators:**
- **Manufacturing PMI**: Purchasing managers' surveys
- **Consumer Confidence**: Spending intentions and job market perceptions
- **Housing Starts/Permits**: Interest rate sensitivity and wealth creation
- **Capital Goods Orders**: Business investment intentions

## Forecasting Methodologies

### Time Series Analysis

**Autoregressive Models (AR):**
- **Concept**: Current values depend on past values
- **AR(1) Model**: Xt = α + βXt-1 + εt
- **Applications**: GDP growth, inflation, interest rate forecasting
- **Limitations**: Assumes constant relationships over time

**Moving Average Models (MA):**
- **Concept**: Current values depend on past error terms
- **MA(1) Model**: Xt = α + εt + θεt-1  
- **Applications**: Smoothing volatile series
- **Combination**: ARMA and ARIMA models for complex patterns

**Vector Autoregression (VAR):**
- **Multi-Variable Models**: Multiple economic variables interact
- **System Approach**: Each variable depends on lags of all variables
- **Applications**: Policy analysis and impulse response functions
- **Complexity**: Requires large datasets and statistical expertise

### Structural Economic Models

**Dynamic Stochastic General Equilibrium (DSGE):**
- **Microfoundations**: Based on optimizing agents (households, firms)
- **Policy Analysis**: Evaluate policy changes and structural reforms
- **Central Bank Usage**: Fed, ECB, and other central banks use DSGE models
- **Limitations**: Complex assumptions and parameter uncertainty

**Macroeconometric Models:**
- **Large-Scale Systems**: Hundreds of equations and variables
- **Historical Examples**: Fair model, Global Insight, Oxford Economics
- **Policy Simulation**: Test alternative policy scenarios
- **Maintenance**: Require continuous updating and calibration

### Leading Indicator Models

**Composite Indicators:**
- **Weighted Combinations**: Multiple indicators combined using statistical weights
- **Conference Board LEI**: Most widely followed composite indicator
- **Regional Variations**: State-level and international versions
- **Custom Indicators**: Firm-specific or sector-specific composites

**Probability Models:**
- **Recession Probability**: Probit/logit models using leading indicators
- **Fed Models**: NY Fed recession probability model
- **Real-Time Updates**: Monthly updates as new data becomes available
- **Communication Tool**: Probability easier to interpret than index levels

## Sectoral and Regional Forecasting

### Industry-Specific Analysis

**Manufacturing Sector:**
- **ISM Manufacturing PMI**: Purchasing managers' survey
- **Industrial Production**: Fed's measure of factory output
- **Capacity Utilization**: Operating rate of industrial capacity
- **New Orders and Inventories**: Forward-looking production indicators

**Services Sector:**
- **ISM Non-Manufacturing PMI**: Services sector survey
- **Employment Trends**: Services employ majority of workers
- **Consumer Spending**: Services consumption patterns
- **Technology Adoption**: Digital transformation impacts

**Housing Sector:**
- **Building Permits and Starts**: Leading indicators of construction
- **Existing Home Sales**: Wealth effects and consumer spending
- **Mortgage Applications**: Credit conditions and affordability
- **Case-Shiller Index**: House price appreciation trends

### Regional Economic Forecasting

**Regional Leading Indicators:**
- **Employment Growth**: Regional job market strength
- **Population Migration**: Domestic migration patterns
- **Housing Markets**: Regional price and construction trends
- **Industry Concentration**: Regional economic specialization

**Metropolitan Statistical Areas (MSAs):**
- **Local Economic Conditions**: City-specific growth patterns
- **Industry Clusters**: Technology, finance, energy centers
- **Policy Impacts**: Local tax and regulation effects
- **Real Estate Markets**: Commercial and residential property trends

## International Economic Forecasting

### Global Economic Indicators

**Purchasing Managers' Indices (PMIs):**
- **Global Manufacturing PMI**: Composite of country PMIs
- **Services PMIs**: Global services sector activity
- **New Export Orders**: International trade momentum
- **Geographic Coverage**: Developed and emerging markets

**International Trade Indicators:**
- **Baltic Dry Index**: Shipping costs and global trade volume
- **Container Throughput**: Port activity and trade flows
- **Trade Balances**: Country-specific export/import trends
- **Currency Movements**: Exchange rate impacts on trade

### Emerging Market Forecasting

**Special Considerations:**
- **Commodity Dependence**: Resource-exporting country vulnerabilities
- **External Financing**: Current account and capital flow dependencies
- **Political Risk**: Policy uncertainty and institutional quality
- **Currency Volatility**: Exchange rate impacts on growth and inflation

**Key Indicators:**
- **Foreign Exchange Reserves**: External vulnerability measures
- **Current Account Balance**: External sustainability metrics
- **Credit Growth**: Financial stability and bubble risks
- **Inflation Expectations**: Monetary policy credibility measures

## Forecast Evaluation and Limitations

### Forecast Accuracy Metrics

**Statistical Measures:**
- **Mean Absolute Error (MAE)**: Average forecast errors
- **Root Mean Square Error (RMSE)**: Penalizes large forecast errors
- **Mean Absolute Percentage Error (MAPE)**: Scale-independent accuracy measure
- **Theil's U-Statistic**: Compares forecast to naive alternatives

**Practical Considerations:**
- **Directional Accuracy**: Getting the direction of change right
- **Turning Point Prediction**: Identifying recessions and recoveries
- **Forecast Horizon**: Accuracy typically declines with longer horizons
- **Real-Time Data**: Revisions to historical data affect model performance

### Forecasting Limitations

**Structural Breaks:**
- **Regime Changes**: Policy shifts, technological changes
- **Lucas Critique**: Economic relationships change with policy changes
- **Model Instability**: Parameters may not be constant over time
- **Historical Precedent**: Unique events (pandemics, wars) difficult to forecast

**Data Limitations:**
- **Measurement Error**: Economic data subject to revisions
- **Publication Lags**: Key indicators available with delays
- **Frequency Mismatches**: Monthly, quarterly, and annual data integration
- **Sample Size**: Limited historical data for rare events

**Behavioral Factors:**
- **Expectation Formation**: How agents form future expectations
- **Herding Behavior**: Collective optimism or pessimism
- **Policy Uncertainty**: Political and regulatory uncertainty effects
- **Black Swan Events**: Low-probability, high-impact events

Economic forecasting combines quantitative analysis with qualitative judgment to provide insights into future economic conditions, recognizing inherent uncertainties and limitations in predicting complex economic systems.`
        }
      ]
    },

    {
      id: "international-economics",
      title: "International Economics",
      description: "Trade theory, exchange rates, and global economic integration",
      order: 4,
      estimatedHours: 3.5,
      content: [
        {
          id: "trade-theory",
          title: "International Trade Theory and Policy",
          description: "Comparative advantage, trade patterns, and trade policy impacts",
          type: "video",
          duration: 30
        },
        {
          id: "exchange-rate-economics",
          title: "Exchange Rate Determination and Policy",
          description: "Currency valuation models and exchange rate regimes",
          type: "text",
          duration: 35,
          textContent: `# Exchange Rate Determination and Policy

## Exchange Rate Fundamentals

### Exchange Rate Definitions

**Nominal Exchange Rate:**
- **Direct Quote**: Home currency per unit of foreign currency (e.g., $1.20 per Euro)
- **Indirect Quote**: Foreign currency per unit of home currency (e.g., €0.83 per Dollar)
- **Spot Rate**: Current exchange rate for immediate delivery
- **Forward Rate**: Exchange rate for future delivery (30, 60, 90 days)

**Real Exchange Rate:**
- **Formula**: Real Rate = Nominal Rate × (Foreign Price Level / Domestic Price Level)
- **Interpretation**: Relative purchasing power comparison
- **Competitiveness Measure**: Higher real rates indicate less competitive exports
- **Long-Term Trends**: Real exchange rate mean reversion tendency

**Effective Exchange Rates:**
- **Trade-Weighted Index**: Basket of currencies weighted by trade volumes
- **Major Currency Index**: G7 or G10 currency basket
- **Broad Index**: Includes emerging market currencies
- **Federal Reserve Indices**: Dollar Index (DXY) and Fed broad/major indices

## Exchange Rate Determination Theories

### Purchasing Power Parity (PPP)

**Absolute PPP:**
- **Law of One Price**: Identical goods should cost the same globally
- **Formula**: S = P*/P (exchange rate = foreign price level / domestic price level)
- **Big Mac Index**: Economist's informal PPP measure using McDonald's prices
- **Limitations**: Transportation costs, trade barriers, non-tradable goods

**Relative PPP:**
- **Inflation Differential Theory**: Exchange rates adjust for inflation differences
- **Formula**: %ΔS = π* - π (currency change = foreign inflation - domestic inflation)
- **Empirical Evidence**: Better long-term than short-term predictor
- **Applications**: Long-term exchange rate forecasting

### Interest Rate Parity Conditions

**Covered Interest Rate Parity (CIP):**
- **No-Arbitrage Condition**: Forward rate eliminates arbitrage opportunities
- **Formula**: (1 + i) = (F/S) × (1 + i*)
- **Forward Premium/Discount**: Forward rate vs. spot rate comparison
- **Empirical Evidence**: Generally holds for liquid currency pairs

**Uncovered Interest Rate Parity (UIP):**
- **Expected Depreciation**: High interest rate currencies expected to depreciate
- **Formula**: E[%ΔS] = i - i* (expected currency change = interest differential)
- **Forward Rate Bias**: Forward rates typically poor predictors of future spots
- **Carry Trade Puzzle**: High interest rate currencies often appreciate

### Balance of Payments Approach

**Current Account Impact:**
- **Trade Balance**: Export/import flows create currency demand/supply
- **Services Balance**: Tourism, transportation, financial services
- **Income Balance**: Investment income, remittances
- **Transfer Balance**: Government transfers, foreign aid

**Capital Account Impact:**
- **Foreign Direct Investment**: Long-term capital flows
- **Portfolio Investment**: Stock and bond investments
- **Other Investment**: Bank loans, trade credits
- **Official Reserves**: Central bank interventions

**Balance of Payments Identity:**
Current Account + Capital Account + Financial Account + Statistical Discrepancy = 0

## Exchange Rate Regimes

### Fixed Exchange Rate Systems

**Currency Board:**
- **Full Backing**: Domestic currency backed 100% by foreign reserves
- **Automatic Adjustment**: Money supply adjusts with reserve flows
- **Examples**: Hong Kong Dollar (vs. USD), Bulgarian Lev (vs. Euro)
- **Benefits**: Credibility, low inflation, trade facilitation
- **Costs**: Loss of monetary policy independence, vulnerability to speculative attacks

**Conventional Peg:**
- **Central Bank Commitment**: Maintain exchange rate within narrow band
- **Reserve Management**: Use foreign reserves to defend peg
- **Adjustable Peg**: Periodic devaluations allowed (Bretton Woods system)
- **Crawling Peg**: Gradual, pre-announced rate adjustments

### Flexible Exchange Rate Systems

**Independent Float:**
- **Market Determination**: Supply and demand determine exchange rate
- **Central Bank Non-Intervention**: No systematic intervention
- **Examples**: US Dollar, Euro, Japanese Yen, British Pound
- **Benefits**: Monetary policy independence, automatic adjustment
- **Costs**: Exchange rate volatility, uncertainty for traders

**Managed Float:**
- **Dirty Float**: Occasional central bank intervention
- **Target Zones**: Informal ranges for exchange rate management
- **Smoothing Operations**: Reduce short-term volatility
- **Most Common Regime**: Many countries use managed floating

### Intermediate Regimes

**Currency Union:**
- **Common Currency**: Multiple countries share single currency
- **Examples**: Eurozone (Euro), West African CFA Franc
- **Benefits**: Eliminate exchange rate risk within union, trade facilitation
- **Requirements**: Economic convergence, fiscal coordination, labor mobility

**Dollarization:**
- **Official Adoption**: Foreign currency becomes legal tender
- **Examples**: Ecuador (USD), El Salvador (USD), Montenegro (Euro)
- **Benefits**: Credibility, inflation control, reduced currency risk
- **Costs**: Loss of seigniorage, lender of last resort function

## Central Bank Intervention

### Intervention Methods

**Spot Market Intervention:**
- **Direct Purchases/Sales**: Buy/sell currency in spot markets
- **Sterilized Intervention**: Offset money supply effects through domestic operations
- **Unsterilized Intervention**: Allow intervention to affect money supply
- **Effectiveness Debate**: Limited empirical evidence of lasting effects

**Forward Market Operations:**
- **Forward Sales/Purchases**: Commitments to buy/sell at future dates
- **Swap Operations**: Simultaneous spot and forward transactions
- **Signaling Effects**: Communicate central bank policy intentions
- **Reserve Requirements**: Lower than spot interventions

### Intervention Objectives

**Exchange Rate Stabilization:**
- **Reduce Volatility**: Smooth short-term fluctuations
- **Prevent Overshooting**: Limit excessive appreciation/depreciation
- **Maintain Competitiveness**: Prevent currency from becoming overvalued
- **Support Export Industries**: Maintain competitive exchange rates

**Market Functioning:**
- **Provide Liquidity**: Market making during stress periods
- **Prevent Disorderly Markets**: Intervene during crisis periods
- **Build Reserves**: Accumulate foreign exchange reserves
- **Signal Policy Stance**: Communicate monetary policy intentions

## Exchange Rate Crises

### Types of Currency Crises

**First Generation Models (Krugman):**
- **Fiscal Dominance**: Unsustainable fiscal deficits monetized
- **Reserve Depletion**: Gradual loss of reserves defending peg
- **Speculative Attack**: Rational attack when reserves approach critical level
- **Examples**: Mexico 1994, Asian countries 1997

**Second Generation Models (Obstfeld):**
- **Multiple Equilibria**: Self-fulfilling crisis expectations
- **Policy Trade-offs**: Government weighs costs of defending peg
- **Credibility Issues**: Market doubts about government commitment
- **Examples**: European Monetary System 1992-93

**Third Generation Models:**
- **Balance Sheet Effects**: Currency mismatches amplify crisis
- **Banking Sector Links**: Currency and banking crises interaction
- **Moral Hazard**: Implicit government guarantees create risk-taking
- **Examples**: Asian Financial Crisis 1997-98, Russia 1998

### Crisis Prevention and Management

**Early Warning Systems:**
- **Leading Indicators**: Current account deficits, reserve adequacy, debt ratios
- **Market-Based Measures**: Currency risk premiums, credit default swaps
- **Institutional Quality**: Governance, policy credibility measures
- **Regional Contagion**: Spillover effects from neighboring countries

**Crisis Management:**
- **IMF Programs**: Conditional lending to restore confidence
- **Capital Controls**: Temporary restrictions on capital outflows
- **Debt Restructuring**: Negotiated reduction in foreign debt burdens
- **Exchange Rate Adjustment**: Devaluation or move to floating rate

## Modern Exchange Rate Challenges

### Reserve Currency Status

**Dollar Dominance:**
- **International Trade**: 60% of global trade invoiced in dollars
- **Foreign Exchange Reserves**: 60% of global reserves held in dollars
- **Financial Markets**: Dollar dominance in international bond and loan markets
- **Network Effects**: Self-reinforcing nature of reserve currency status

**Challenges to Dollar:**
- **Euro**: Second most important reserve currency (20% of reserves)
- **Chinese Yuan**: Gradual internationalization efforts
- **Digital Currencies**: Central bank digital currencies (CBDCs)
- **Geopolitical Factors**: Sanctions and weaponization concerns

### Emerging Market Challenges

**Trilemma/Impossible Trinity:**
- **Three Policy Goals**: Fixed exchange rate, independent monetary policy, free capital flows
- **Cannot Achieve All Three**: Must sacrifice one objective
- **Policy Choices**: Most countries choose monetary independence and flexible rates
- **China Exception**: Capital controls allow fixed rate and monetary independence

**Currency Wars Concerns:**
- **Competitive Devaluation**: Countries devalue to boost exports
- **Beggar-Thy-Neighbor**: Export advantages at trading partners' expense
- **Policy Coordination**: G7/G20 efforts to prevent currency manipulation
- **Trade Policy Responses**: Tariffs and other trade measures as retaliation

Understanding exchange rate determination and policy is crucial for international business, investment decisions, and macroeconomic policy analysis in an increasingly integrated global economy.`
        }
      ]
    }
  ],

  assignments: [
    {
      id: "economic-analysis-project",
      title: "Comprehensive Economic Analysis Project",
      description: "Conduct thorough economic analysis of a country or region including forecasting and policy recommendations",
      type: "project",
      dueWeeks: 6,
      maxScore: 100,
      instructions: `# Economic Analysis Project

## Objective
Analyze the economic conditions and outlook for a selected country or region using economic theory and forecasting techniques.

## Requirements
1. **Country/Region Selection**: Choose developed, emerging, or frontier market
2. **Macroeconomic Analysis**: GDP, inflation, employment, fiscal/monetary policy
3. **Sector Analysis**: Key industries and competitive positioning
4. **International Aspects**: Trade, capital flows, exchange rate analysis
5. **Forecasting**: Economic projections using multiple methodologies
6. **Policy Recommendations**: Fiscal, monetary, and structural policy advice

## Deliverables
- Executive summary with key findings and recommendations
- Comprehensive analysis report (25-30 pages)
- Economic data analysis and charts
- Forecasting models and sensitivity analysis
- Policy memo with implementation recommendations

## Evaluation Criteria
- Economic theory application (25%)
- Data analysis and forecasting quality (30%)
- Policy insights and recommendations (25%)
- Communication and presentation (20%)`,
      submissionFormat: "pdf",
      resources: [
        "Economic databases (IMF, World Bank, OECD)",
        "Central bank publications and data",
        "Economic forecasting software tutorials"
      ]
    }
  ],

  finalExam: {
    id: "business-economics-final",
    title: "Business Economics Final Exam",
    description: "Comprehensive examination of economic principles and their business applications",
    timeLimit: 120,
    passingScore: 80,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "In oligopoly markets, firms are most likely to:",
        options: [
          "Ignore competitors' actions when making decisions",
          "Consider competitors' likely responses to their actions",
          "Compete solely on price with identical products",
          "Have no market power due to competition"
        ],
        correctAnswer: 1,
        explanation: "Oligopoly is characterized by strategic interdependence, where firms must consider how competitors will likely respond to their pricing and output decisions."
      },
      {
        id: "final-q2",
        type: "multiple-choice",
        question: "An inverted yield curve typically signals:",
        options: [
          "Rising inflation expectations",
          "Expansionary monetary policy ahead",
          "Potential economic recession",
          "Increasing government budget deficits"
        ],
        correctAnswer: 2,
        explanation: "An inverted yield curve, where short-term rates exceed long-term rates, has historically been a reliable predictor of economic recessions within 12-24 months."
      },
      {
        id: "final-q3",
        type: "multiple-choice",
        question: "According to purchasing power parity theory, if Country A has higher inflation than Country B, then Country A's currency should:",
        options: [
          "Appreciate relative to Country B's currency",
          "Depreciate relative to Country B's currency", 
          "Remain unchanged relative to Country B's currency",
          "Become more volatile relative to Country B's currency"
        ],
        correctAnswer: 1,
        explanation: "PPP theory suggests that higher inflation countries should see their currencies depreciate to maintain equivalent purchasing power across countries."
      }
    ]
  },

  quizzes: []
};